import { EEG_SERVICES, NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { CLIENT_TYPE_LABELS, clientTypeOfAge, CoarseDisorderGroupKey, coarseDisorderGroupOf, disorderDrillLabelOf } from "./teamKpisOutcomes";
import { ServiceProvidedSpan, SpansByTreatment } from "./teamKpisSpans";

// Cartões "Clientes ativos" (Total, NFB e Psicologia) — snapshot de TODOS os tratamentos com estado
// "Ativo", sem filtro de data. Disciplina = ≥1 sessão realizada do serviço (33/34); um tratamento com
// ambas aparece nos dois cartões de disciplina, e no cartão do total conta UMA vez (segmento "Ambos").
// Fases portadas do (extinto) Treatment Performance Dashboard:
//   NFB: Fase 2 = ≥2 qEEGs realizados em dias distintos; Psicologia: fase do período de
//   services_provided mais recente com cadência real (weekly=1, monthly=2; not_applicable ignorado).

export const ACTIVE_TREATMENT_STATUS = "all_good";

// start_date opcional: o fetch dos terminados não o traz, e só as fases do NFB o leem.
export type ActiveSessionItem = { service: number | null; status: string | null; start_date?: string | null };

export type ActiveTreatmentItem = {
    id: number;
    diagnose_main?: { id: number | string; name?: string | null } | null;
    client?: { client_id?: string | null } | null;
    age_when_started?: number | null;
    date_of_start?: string | null;
    sessions?: Array<ActiveSessionItem> | null;
};

export type ActivesDiscipline = "nfb" | "psy";

const disciplineServiceOf = (discipline: ActivesDiscipline) => {
    const service = discipline === "nfb" ? NFB_SERVICE : PSY_SERVICE;
    return service;
};

const hasDoneSessionOfService = (sessions: Array<ActiveSessionItem> | null | undefined, service: number) => {
    const hasDone = (sessions || []).some((session) => session.status === "done" && Number(session.service) === service);
    return hasDone;
};

const doneDayKeysOfServices = (sessions: Array<ActiveSessionItem> | null | undefined, services: Array<number>) => {
    const dayKeys = (sessions || [])
        .filter((session) => session.status === "done" && session.service !== null && services.includes(Number(session.service)))
        .map((session) => (session.start_date ? session.start_date.slice(0, 10) : null))
        .filter((dayKey): dayKey is string => dayKey !== null);
    const distinctDayKeys = Array.from(new Set(dayKeys));
    return distinctDayKeys;
};

// Fase do NFB a partir das sessões — exposta porque as colunas "Ativos F1/F2" da tabela das gestoras
// aplicam a MESMA regra do cartão a tratamentos que lá chegam com outro tipo.
export const nfbPhaseOfSessions = (sessions: Array<ActiveSessionItem> | null | undefined) => {
    const eegDayKeys = doneDayKeysOfServices(sessions, EEG_SERVICES);
    const phase = eegDayKeys.length >= 2 ? 2 : 1;
    return phase;
};

export const nfbActivesOfPhase = <TreatmentType extends { sessions?: Array<ActiveSessionItem> | null }>(
    treatments: Array<TreatmentType>,
    phase: 1 | 2
) => {
    const ofPhase = treatments.filter((treatment) => nfbPhaseOfSessions(treatment.sessions) === phase);
    return ofPhase;
};

const nfbActivePhaseOf = (treatment: ActiveTreatmentItem) => {
    const phase = nfbPhaseOfSessions(treatment.sessions);
    return phase;
};

export const psyActivePhaseOf = (spans: Array<ServiceProvidedSpan> | undefined) => {
    const cadencedSpans = (spans || []).filter(
        (span) =>
            Number(span.service) === PSY_SERVICE &&
            (span.session_periodicity === "weekly" || span.session_periodicity === "monthly") &&
            span.date_begin !== null
    );
    if (cadencedSpans.length === 0) return 1;
    const mostRecentSpan = [...cadencedSpans].sort((firstSpan, secondSpan) =>
        (secondSpan.date_begin || "").localeCompare(firstSpan.date_begin || "")
    )[0];
    const phase = mostRecentSpan.session_periodicity === "monthly" ? 2 : 1;
    return phase;
};

export type ActivesPhaseSummary = { count: number; byGroup: Partial<Record<CoarseDisorderGroupKey, number>> };
export type ActivesSummary = { total: number; phase1: ActivesPhaseSummary; phase2: ActivesPhaseSummary };

const activeDisciplineItemsOf = (treatmentItems: Array<ActiveTreatmentItem>, discipline: ActivesDiscipline) => {
    const disciplineService = disciplineServiceOf(discipline);
    const disciplineItems = treatmentItems.filter((treatment) => hasDoneSessionOfService(treatment.sessions, disciplineService));
    return disciplineItems;
};

const activePhaseOf = (treatment: ActiveTreatmentItem, discipline: ActivesDiscipline, spansByTreatment: SpansByTreatment) => {
    const phase = discipline === "nfb" ? nfbActivePhaseOf(treatment) : psyActivePhaseOf(spansByTreatment[treatment.id]);
    return phase;
};

export const activesSummaryOf = (treatmentItems: Array<ActiveTreatmentItem>, discipline: ActivesDiscipline, spansByTreatment: SpansByTreatment) => {
    const summary: ActivesSummary = { total: 0, phase1: { count: 0, byGroup: {} }, phase2: { count: 0, byGroup: {} } };
    activeDisciplineItemsOf(treatmentItems, discipline).forEach((treatment) => {
        const phase = activePhaseOf(treatment, discipline, spansByTreatment);
        const groupKey = coarseDisorderGroupOf(treatment.diagnose_main?.id);
        summary.total += 1;
        const phaseSummary = phase === 2 ? summary.phase2 : summary.phase1;
        phaseSummary.count += 1;
        phaseSummary.byGroup[groupKey] = (phaseSummary.byGroup[groupKey] || 0) + 1;
    });
    return summary;
};

// Drill dos cartões de fase: os MESMOS predicados do resumo (disciplina + fase), por isso o nº de
// linhas reconcilia com o número do cartão.
export type ActivesDrillRow = {
    id: number;
    clientNumber: string | null;
    clientTypeLabel: string;
    disorderLabel: string;
    dateOfStart: string | null;
    doneSessions: number;
};

export const activesPhaseDrillRowsOf = (
    treatmentItems: Array<ActiveTreatmentItem>,
    discipline: ActivesDiscipline,
    spansByTreatment: SpansByTreatment,
    phase: 1 | 2
) => {
    const disciplineService = disciplineServiceOf(discipline);
    const rows = activeDisciplineItemsOf(treatmentItems, discipline)
        .filter((treatment) => activePhaseOf(treatment, discipline, spansByTreatment) === phase)
        .map((treatment) => {
            const row: ActivesDrillRow = {
                id: treatment.id,
                clientNumber: treatment.client?.client_id ?? null,
                clientTypeLabel: CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)],
                disorderLabel: disorderDrillLabelOf(treatment.diagnose_main),
                dateOfStart: treatment.date_of_start ?? null,
                doneSessions: doneSessionsCountOfService(treatment.sessions, disciplineService),
            };
            return row;
        });
    return rows;
};

// ---- Cartão "Clientes ativos — Total": união NFB ∪ Psicologia, com a sobreposição visível ----
// Mesmo universo dos dois cartões de disciplina (≥1 sessão realizada), mas cada tratamento conta UMA
// vez, no segmento que descreve o que faz. Por construção:
//   Só NFB + Ambos = total do cartão NFB;  Só Psi + Ambos = total do cartão Psicologia.

export type ActivesDisciplineSegment = "onlyNfb" | "onlyPsy" | "both";

export type TotalActivesSummary = {
    total: number;
    onlyNfb: ActivesPhaseSummary;
    onlyPsy: ActivesPhaseSummary;
    both: ActivesPhaseSummary;
};

const disciplineSegmentOf = (treatment: ActiveTreatmentItem) => {
    const hasNfbDone = hasDoneSessionOfService(treatment.sessions, NFB_SERVICE);
    const hasPsyDone = hasDoneSessionOfService(treatment.sessions, PSY_SERVICE);
    if (hasNfbDone && hasPsyDone) return "both";
    if (hasNfbDone) return "onlyNfb";
    if (hasPsyDone) return "onlyPsy";
    const segment: ActivesDisciplineSegment | null = null;
    return segment;
};

export const totalActivesSummaryOf = (treatmentItems: Array<ActiveTreatmentItem>) => {
    const summary: TotalActivesSummary = {
        total: 0,
        onlyNfb: { count: 0, byGroup: {} },
        onlyPsy: { count: 0, byGroup: {} },
        both: { count: 0, byGroup: {} },
    };
    treatmentItems.forEach((treatment) => {
        const segment = disciplineSegmentOf(treatment);
        if (segment === null) return;
        const groupKey = coarseDisorderGroupOf(treatment.diagnose_main?.id);
        summary.total += 1;
        const segmentSummary = summary[segment];
        segmentSummary.count += 1;
        segmentSummary.byGroup[groupKey] = (segmentSummary.byGroup[groupKey] || 0) + 1;
    });
    return summary;
};

// Drill das caixas do total: a lista mistura disciplinas, por isso as sessões realizadas vêm em duas
// colunas — uma só seria ambígua.
export type TotalActivesDrillRow = {
    id: number;
    clientNumber: string | null;
    clientTypeLabel: string;
    disorderLabel: string;
    dateOfStart: string | null;
    doneNfbSessions: number;
    donePsySessions: number;
};

const doneSessionsCountOfService = (sessions: Array<ActiveSessionItem> | null | undefined, service: number) => {
    const count = (sessions || []).filter((session) => session.status === "done" && Number(session.service) === service).length;
    return count;
};

export const totalActivesDrillRowsOf = (treatmentItems: Array<ActiveTreatmentItem>, segment: ActivesDisciplineSegment) => {
    const rows = treatmentItems
        .filter((treatment) => disciplineSegmentOf(treatment) === segment)
        .map((treatment) => {
            const row: TotalActivesDrillRow = {
                id: treatment.id,
                clientNumber: treatment.client?.client_id ?? null,
                clientTypeLabel: CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)],
                disorderLabel: disorderDrillLabelOf(treatment.diagnose_main),
                dateOfStart: treatment.date_of_start ?? null,
                doneNfbSessions: doneSessionsCountOfService(treatment.sessions, NFB_SERVICE),
                donePsySessions: doneSessionsCountOfService(treatment.sessions, PSY_SERVICE),
            };
            return row;
        });
    return rows;
};

// Ids que alimentam o fetch (em chunks) dos services_provided: os ativos com ≥1 sessão realizada de
// NFB ou de Psicologia. Só estes podem contar em qualquer cartão ou coluna, por isso é o mínimo
// necessário — os spans servem a fase da Psicologia E a coluna "Ativos" das duas tabelas.
export const activeTreatmentIdsForSpansOf = (treatmentItems: Array<ActiveTreatmentItem>) => {
    const ids = treatmentItems.filter((treatment) => disciplineSegmentOf(treatment) !== null).map((treatment) => treatment.id);
    return ids;
};
